import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin,
  Calendar as CalendarIcon,
  Clock,
  Search as SearchIcon,
  AlertCircle,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

// Shop hours per pickup point (minutes-from-midnight, 30-min grid)
// Udupi:  07:30 → 19:30
// Manipal: 09:00 → 19:30
const SHOP_HOURS = {
  Udupi: { startMin: 7 * 60 + 30, endMin: 19 * 60 + 30 },
  Manipal: { startMin: 9 * 60, endMin: 19 * 60 + 30 },
};

const PICKUP_OPTIONS = Object.keys(SHOP_HOURS);

const minToValue = (m) =>
  `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(
    2,
    "0"
  )}`;

const minToLabel = (m) => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const hour12 = ((h + 11) % 12) + 1;
  return `${String(hour12).padStart(2, "0")}:${String(mm).padStart(2, "0")} ${
    h < 12 ? "AM" : "PM"
  }`;
};

const valueToMin = (v) => {
  if (!v) return null;
  const [h, m] = v.split(":").map(Number);
  return h * 60 + m;
};

const slotsFor = (pickup) => {
  const { startMin, endMin } = SHOP_HOURS[pickup];
  const slots = [];
  for (let m = startMin; m <= endMin; m += 30) {
    slots.push({ value: minToValue(m), label: minToLabel(m), min: m });
  }
  return slots;
};

// Get current date/time in India Standard Time (Asia/Kolkata) regardless of
// the user's device timezone. Returns { todayISO, nowMins }.
const getIST = () => {
  const d = new Date();
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(d).map((p) => [p.type, p.value])
  );
  const todayISO = `${parts.year}-${parts.month}-${parts.day}`;
  const nowMins = parseInt(parts.hour, 10) * 60 + parseInt(parts.minute, 10);
  return { todayISO, nowMins };
};

const roundUpTo30 = (m) => Math.ceil(m / 30) * 30;

const HomeSearch = () => {
  const navigate = useNavigate();

  // Pickup Point defaults to Udupi; all 4 date/time fields start EMPTY
  // and the customer must choose them manually.
  const [pickup, setPickup] = useState("Udupi");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [error, setError] = useState(null);

  // Re-evaluate IST on every render tick that depends on it; cheap enough.
  const { todayISO, nowMins } = useMemo(() => getIST(), [startDate, pickup]);

  const timeSlots = useMemo(() => slotsFor(pickup), [pickup]);

  // When pickup changes, CLEAR any times that are no longer valid for the
  // new shop hours (do not silently clamp).
  useEffect(() => {
    const { startMin, endMin } = SHOP_HOURS[pickup];
    if (startTime) {
      const m = valueToMin(startTime);
      if (m < startMin || m > endMin) setStartTime("");
    }
    if (endTime) {
      const m = valueToMin(endTime);
      if (m < startMin || m > endMin) setEndTime("");
    }
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup]);

  // If start date changes to a date later than end date, clear end date.
  useEffect(() => {
    if (startDate && endDate && endDate < startDate) {
      setEndDate("");
      setEndTime("");
    }
  }, [startDate, endDate]);

  // Pickup-time options: shop-hour slots, with today's past slots filtered
  // out when startDate === IST today.
  const startSlots = useMemo(() => {
    if (!startDate) return timeSlots;
    if (startDate !== todayISO) return timeSlots;
    const minAllowed = Math.max(
      SHOP_HOURS[pickup].startMin,
      roundUpTo30(nowMins)
    );
    return timeSlots.filter((s) => s.min >= minAllowed);
  }, [startDate, timeSlots, todayISO, pickup, nowMins]);

  // Drop-off-time options:
  //  • Shop-hour slots for the pickup point.
  //  • If endDate === today, filter today's past times.
  //  • If endDate === startDate, options must be strictly AFTER startTime.
  const endSlots = useMemo(() => {
    let slots = timeSlots;
    if (endDate && endDate === todayISO) {
      const minAllowed = Math.max(
        SHOP_HOURS[pickup].startMin,
        roundUpTo30(nowMins)
      );
      slots = slots.filter((s) => s.min >= minAllowed);
    }
    if (startDate && endDate && startDate === endDate && startTime) {
      const startM = valueToMin(startTime);
      slots = slots.filter((s) => s.min > startM);
    }
    return slots;
  }, [timeSlots, endDate, todayISO, pickup, nowMins, startDate, startTime]);

  // If a previously-chosen startTime/endTime becomes invalid as the user
  // changes other fields, clear it so the customer picks a valid one.
  useEffect(() => {
    if (!startTime) return;
    const stillValid = startSlots.some((s) => s.value === startTime);
    if (!stillValid) setStartTime("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startSlots]);

  useEffect(() => {
    if (!endTime) return;
    const stillValid = endSlots.some((s) => s.value === endTime);
    if (!stillValid) setEndTime("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endSlots]);

  const onSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (!pickup || !startDate || !startTime || !endDate || !endTime) {
      setError("Please select your start and end date and time.");
      return;
    }

    // IST now
    const { todayISO: istToday, nowMins: istNow } = getIST();

    if (startDate < istToday) {
      setError("Please select a current or future date and time.");
      return;
    }
    if (
      startDate === istToday &&
      valueToMin(startTime) < roundUpTo30(istNow)
    ) {
      setError("Please select a current or future date and time.");
      return;
    }
    if (endDate < startDate) {
      setError("Drop-off date cannot be before pickup date.");
      return;
    }
    if (startDate === endDate && valueToMin(endTime) <= valueToMin(startTime)) {
      setError("Drop-off time must be after pickup time.");
      return;
    }

    const { startMin, endMin } = SHOP_HOURS[pickup];
    const sM = valueToMin(startTime);
    const eM = valueToMin(endTime);
    if (sM < startMin || sM > endMin || eM < startMin || eM > endMin) {
      setError(
        `For ${pickup}, pickup and drop-off times must be between ${minToLabel(
          startMin
        )} and ${minToLabel(endMin)}.`
      );
      return;
    }

    const params = new URLSearchParams({
      pickup,
      startDate,
      startTime,
      endDate,
      endTime,
    });
    navigate(`/search?${params.toString()}`);
  };

  const shopHoursLabel = `${minToLabel(SHOP_HOURS[pickup].startMin)} – ${minToLabel(
    SHOP_HOURS[pickup].endMin
  )}`;

  const minStartDate = todayISO;
  const minEndDate = startDate || todayISO;

  return (
    <section
      aria-labelledby="home-search-heading"
      data-testid="home-search-section"
      className="relative bg-gradient-to-br from-neutral-900 via-neutral-900 to-neutral-800 pt-10 sm:pt-16 pb-10 sm:pb-16"
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none [background-image:radial-gradient(circle_at_20%_20%,rgba(249,115,22,0.25),transparent_40%),radial-gradient(circle_at_80%_60%,rgba(249,115,22,0.15),transparent_45%)]" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center sm:text-left max-w-2xl mx-auto sm:mx-0">
          <span className="inline-block text-orange-400 font-semibold text-[11px] sm:text-sm uppercase tracking-[0.2em] mb-2">
            RR Bros Bike Rental &bull; Udupi &bull; Manipal
          </span>
          <h1
            id="home-search-heading"
            className="text-white font-black text-3xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight"
          >
            Search your <span className="text-orange-500">next ride</span>
          </h1>
          <p className="mt-2 sm:mt-3 text-neutral-300 text-sm sm:text-base">
            Live scooter availability &amp; pricing in Udupi and Manipal.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="home-search-form"
          noValidate
          className="mt-5 sm:mt-8 bg-white rounded-2xl shadow-2xl border border-white/10 p-4 sm:p-6 space-y-4 sm:space-y-5"
        >
          {/* Pickup Point */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5 block">
              Pickup Point
            </label>
            <Select value={pickup} onValueChange={setPickup}>
              <SelectTrigger
                className="h-14 text-base font-semibold bg-neutral-50 rounded-xl border-neutral-200 px-4"
                data-testid="home-search-pickup-trigger"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <MapPin className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                {PICKUP_OPTIONS.map((loc) => (
                  <SelectItem
                    key={loc}
                    value={loc}
                    data-testid={`home-pickup-${loc.toLowerCase()}`}
                    className="text-base py-3"
                  >
                    {loc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-neutral-500 mt-1.5">
              Shop hours: <span className="font-semibold">{shopHoursLabel}</span>
            </p>
          </div>

          {/* Pickup */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
              Pickup
            </p>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <label
                htmlFor="home-start-date"
                className="relative flex items-center h-14 bg-neutral-50 border border-neutral-200 rounded-xl px-3 focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-transparent cursor-pointer"
              >
                <CalendarIcon className="w-5 h-5 text-orange-500 flex-shrink-0 mr-2" />
                {!startDate && (
                  <span className="absolute left-11 text-sm sm:text-base font-semibold text-neutral-400 pointer-events-none">
                    Start Date
                  </span>
                )}
                <input
                  id="home-start-date"
                  type="date"
                  min={minStartDate}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  aria-label="Start date"
                  data-testid="home-start-date"
                  className={`w-full bg-transparent text-sm sm:text-base font-semibold outline-none appearance-none ${
                    startDate ? "text-neutral-900" : "text-transparent"
                  }`}
                />
              </label>
              <Select
                value={startTime}
                onValueChange={setStartTime}
              >
                <SelectTrigger
                  className="h-14 bg-neutral-50 rounded-xl border-neutral-200 px-3 text-sm sm:text-base font-semibold data-[placeholder]:text-neutral-400"
                  aria-label="Start time"
                  data-testid="home-start-time-trigger"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Clock className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <SelectValue placeholder="Start Time" />
                  </div>
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {startSlots.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-neutral-500">
                      No slots left today — pick a future date.
                    </div>
                  ) : (
                    startSlots.map((t) => (
                      <SelectItem
                        key={`hs-${t.value}`}
                        value={t.value}
                        className="text-base py-3"
                      >
                        {t.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Drop-off */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
              Drop-off
            </p>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <label
                htmlFor="home-end-date"
                className="relative flex items-center h-14 bg-neutral-50 border border-neutral-200 rounded-xl px-3 focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-transparent cursor-pointer"
              >
                <CalendarIcon className="w-5 h-5 text-orange-500 flex-shrink-0 mr-2" />
                {!endDate && (
                  <span className="absolute left-11 text-sm sm:text-base font-semibold text-neutral-400 pointer-events-none">
                    End Date
                  </span>
                )}
                <input
                  id="home-end-date"
                  type="date"
                  min={minEndDate}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  aria-label="End date"
                  data-testid="home-end-date"
                  className={`w-full bg-transparent text-sm sm:text-base font-semibold outline-none appearance-none ${
                    endDate ? "text-neutral-900" : "text-transparent"
                  }`}
                />
              </label>
              <Select value={endTime} onValueChange={setEndTime}>
                <SelectTrigger
                  className="h-14 bg-neutral-50 rounded-xl border-neutral-200 px-3 text-sm sm:text-base font-semibold data-[placeholder]:text-neutral-400"
                  aria-label="End time"
                  data-testid="home-end-time-trigger"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Clock className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <SelectValue placeholder="End Time" />
                  </div>
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {endSlots.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-neutral-500">
                      No valid slots — pick a later drop-off date.
                    </div>
                  ) : (
                    endSlots.map((t) => (
                      <SelectItem
                        key={`he-${t.value}`}
                        value={t.value}
                        className="text-base py-3"
                      >
                        {t.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 flex items-start gap-2 text-sm"
              data-testid="home-search-error"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            data-testid="home-search-submit"
            className="w-full h-14 rounded-xl bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-500 text-neutral-900 font-black text-base uppercase tracking-wider inline-flex items-center justify-center gap-2 shadow-lg hover:shadow-yellow-300/40 transition-all"
          >
            <SearchIcon className="w-5 h-5" />
            Search
          </button>

          <p className="text-[11px] text-neutral-500 text-center">
            Live availability & pricing from our rental system.
          </p>
        </form>
      </div>
    </section>
  );
};

export default HomeSearch;
